// =============================================================================
// Module Core - Repository: Organization
// Accès DB pour les organisations
// =============================================================================

import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Organization } from '../types';

async function toFunctionError(error: unknown): Promise<Error> {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = await error.context.json();
      const message = typeof payload?.error === 'string' ? payload.error : error.message;
      const step = typeof payload?.step === 'string' ? payload.step : null;
      return new Error(step ? `${message} (${step})` : message);
    } catch {
      return new Error(error.message);
    }
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error('Erreur inconnue');
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ORGANIZATION BY ID
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOrganization(orgId: string): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('core_organizations')
    .select('*')
    .eq('id', orgId)
    .single();

  if (error) {
    console.error('Error fetching organization:', error);
    return null;
  }

  return data as Organization;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH USER ORGANIZATIONS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchUserOrganizations(userId: string): Promise<Organization[]> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select(`
      org_id,
      core_organizations (
        id,
        name,
        slug,
        logo_url,
        created_at,
        updated_at,
        created_by,
        updated_by
      )
    `)
    .eq('user_id', userId)
    .eq('status', 'active');

  if (error) {
    console.error('Error fetching user organizations:', error);
    return [];
  }

  return (data || [])
    .map((row) => row.core_organizations as unknown as Organization)
    .filter((org): org is Organization => org !== null);
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE ORGANIZATION (via Edge Function)
// ─────────────────────────────────────────────────────────────────────────────
export async function createOrganization(name: string, slug: string): Promise<Organization | null> {
  const { data, error } = await supabase.functions.invoke('create-organization', {
    body: { name, slug },
  });

  if (error) {
    console.error('Error creating organization:', error);
    throw await toFunctionError(error);
  }

  return data?.organization as Organization || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE ORGANIZATION
// ─────────────────────────────────────────────────────────────────────────────
export async function updateOrganization(
  orgId: string,
  updates: Partial<Pick<Organization, 'name' | 'logo_url'>>
): Promise<boolean> {
  const { error } = await supabase
    .from('core_organizations')
    .update(updates)
    .eq('id', orgId);

  if (error) {
    console.error('Error updating organization:', error);
    return false;
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE ORGANIZATION
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteOrganization(orgId: string): Promise<void> {
  const { error } = await supabase
    .from('core_organizations')
    .delete()
    .eq('id', orgId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE ORGANIZATION (simplified — used by OrgSwitcher, no slug needed)
// ─────────────────────────────────────────────────────────────────────────────
export async function createOrganizationViaEdgeFunction(name: string): Promise<any> {
  const { data, error } = await supabase.functions.invoke('create-organization', {
    body: { name },
  });

  if (error) {
    throw await toFunctionError(error);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
}
